/**
 * New console — overlays.
 *
 * Three shapes, one mechanism. A `Modal` is for something that must be answered before
 * carrying on; a `Panel` slides in from the right for something to read or adjust
 * beside the screen you were already on; a `Sheet` rises from the bottom edge, the way a
 * phone's own menus do. The prototype uses both and they behave the
 * same way, so they share everything except which edge they are anchored to.
 *
 * Portalled into the `.nt` root rather than <body>: the tokens live on that element,
 * so an overlay outside it would render unthemed — the same mistake that left the
 * date picker painting a white calendar on a dark shell.
 *
 * Escape closes, a click on the scrim closes, and focus moves into the overlay on
 * open and back to whatever opened it on close. Focus is also kept inside while the
 * overlay is up, because a dialog you can Tab out of behind is worse than no dialog.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { IconX } from './Icons';
import { Button, IconButton } from './Primitives';

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

const useOverlayBehaviour = (onClose, boxRef) => {
  const opener = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    opener.current = document.activeElement;
    const box = boxRef.current;
    if (box) {
      /* Prefer a field the person can type in. The close button is first in the
         DOM, and focusing it on every parent re-render (a new `onClose` identity)
         is how a character typed in Report an issue jumped to the cross. */
      const firstField = box.querySelector('input:not([disabled]),textarea:not([disabled]),select:not([disabled])');
      const first = firstField || box.querySelector(FOCUSABLE);
      (first || box).focus();
    }
    /* The page behind must not scroll under the overlay. */
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onCloseRef.current(); return; }
      if (e.key !== 'Tab' || !boxRef.current) return;
      const items = Array.from(boxRef.current.querySelectorAll(FOCUSABLE)).filter((n) => n.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);

    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prev;
      if (opener.current && opener.current.focus) opener.current.focus();
    };
    // Open/close only. `onClose` is read from a ref so typing in the dialog does
    // not re-run this and steal focus back to the first button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

const host = () => document.querySelector('.nt') || document.body;

const Shell = ({ kind, onClose, children, labelledBy, wide }) => {
  const boxRef = useRef(null);
  useOverlayBehaviour(onClose, boxRef);

  const onScrim = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div className={`nt-scrim-full ${kind}`} onClick={onScrim}>
      <div
        className={`nt-${kind}${wide ? ' wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        ref={boxRef}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>,
    host()
  );
};

export const Modal = ({ title, onClose, children, footer, wide }) => (
  <Shell kind="modal" onClose={onClose} labelledBy="nt-ov-title" wide={wide}>
    <div className="nt-ov-head">
      <h3 id="nt-ov-title">{title}</h3>
      <span className="nt-spacer" />
      <IconButton onClick={onClose} aria-label="Close"><IconX /></IconButton>
    </div>
    <div className="nt-ov-body">{children}</div>
    {footer ? <div className="nt-ov-foot">{footer}</div> : null}
  </Shell>
);

export const Panel = ({ title, onClose, children, footer }) => (
  <Shell kind="panel" onClose={onClose} labelledBy="nt-ov-title">
    <div className="nt-ov-head">
      <h3 id="nt-ov-title">{title}</h3>
      <span className="nt-spacer" />
      <IconButton onClick={onClose} aria-label="Close"><IconX /></IconButton>
    </div>
    <div className="nt-ov-body">{children}</div>
    {footer ? <div className="nt-ov-foot">{footer}</div> : null}
  </Shell>
);

export const Sheet = ({ title, onClose, children }) => (
  <Shell kind="sheet" onClose={onClose} labelledBy="nt-ov-title">
    <div className="nt-ov-head">
      <h3 id="nt-ov-title">{title}</h3>
      <span className="nt-spacer" />
      <IconButton onClick={onClose} aria-label="Close"><IconX /></IconButton>
    </div>
    <div className="nt-ov-body">{children}</div>
  </Shell>
);

/**
 * Confirm before something consequential. States what will happen rather than asking
 * "are you sure", which tells nobody anything and reads identically for every action.
 *
 * `typeToConfirm` asks for a word before the button will work — the classic reviewer
 * uses the literal "confirm" for the same purpose. It is not decoration: it defeats
 * the muscle memory of clicking through a dialog that looks like every other dialog,
 * which is exactly how the wrong row gets deleted. Reserve it for what cannot be
 * undone; on a reversible action it is only friction.
 *
 * The match is trimmed and case-insensitive. Demanding exact case would fail people
 * who type carefully and correctly.
 */
export const Confirm = ({
  title, body, confirmLabel = 'Confirm', onConfirm, onClose, danger, typeToConfirm
}) => {
  const [typed, setTyped] = useState('');
  const ready = !typeToConfirm
    || typed.trim().toLowerCase() === String(typeToConfirm).trim().toLowerCase();

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <span className="nt-spacer" />
          <Button
            variant={danger ? 'danger' : 'primary'}
            disabled={!ready}
            onClick={() => { onConfirm(); onClose(); }}
          >
            {confirmLabel}
          </Button>
        </>
      )}
    >
      <p style={{ margin: 0, color: 'var(--nt-ink-2)', lineHeight: 1.65 }}>{body}</p>

      {typeToConfirm ? (
        <div className="nt-field nt-mt-16">
          <label htmlFor="nt-confirm-word">
            Type <b>{typeToConfirm}</b> to enable the button
          </label>
          <input
            id="nt-confirm-word"
            className="nt-input"
            value={typed}
            placeholder={typeToConfirm}
            autoComplete="off"
            autoFocus
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && ready) { onConfirm(); onClose(); } }}
          />
        </div>
      ) : null}
    </Modal>
  );
};

export default Modal;
